import React, { useState, useEffect, useRef } from 'react';
import PlatformFooter from '../../components/PlatformFooter';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, CalendarDays, Building2, Link2, BarChart3,
  CreditCard, Settings, LogOut, ChevronRight, Menu, X,
  ChevronDown, Check, Zap, Sun, Moon, LayoutGrid, Camera, Users, ShieldCheck, QrCode,
  Plug, AlertTriangle, Clock, Activity, User, Contact, Percent
} from 'lucide-react';
import NotificationBell from '../../components/einvoice/NotificationBell';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const NAV_ITEMS = [
  { label: 'DASHBOARD', icon: LayoutDashboard, path: '/einvoice/portal/dashboard', permKey: 'dashboard' },
  { label: 'INVOICES', icon: FileText, path: '/einvoice/portal/invoices', permKey: 'invoices' },
  { label: 'ADJUSTMENT (CN/DN)', icon: ShieldCheck, path: '/einvoice/portal/adjustment', permKey: 'adjustments' },
  { label: 'OWNER PAYMENT', icon: Users, path: '/einvoice/portal/owner-payments', permKey: 'owner_payments' },
  { label: 'BOOKINGS', icon: CalendarDays, path: '/einvoice/portal/bookings', permKey: 'bookings' },
  { label: 'PROPERTIES', icon: Building2, path: '/einvoice/portal/properties', permKey: 'properties' },
  { label: 'CUSTOMERS', icon: Contact, path: '/einvoice/portal/customers', permKey: 'customers' },
  { label: 'INTEGRATIONS', icon: Link2, path: '/einvoice/portal/integrations', permKey: 'integrations' },
  { label: 'OTA COMMISSION', icon: Percent, path: '/einvoice/portal/ota-commission', permKey: 'integrations' },
  { label: 'QR e-INVOICE', icon: QrCode, path: '/einvoice/portal/qr-invoice', permKey: 'qr_einvoice' },
  { label: 'REPORTS', icon: BarChart3, path: '/einvoice/portal/reports', permKey: 'reports' },
  { label: 'AUDIT TRAIL', icon: Activity, path: '/einvoice/portal/audit-trail', permKey: 'audit_trail' },
  { label: 'BILLING', icon: CreditCard, path: '/einvoice/portal/billing', permKey: 'billing' },
  { label: 'MAPPING BUILDER', icon: Zap, path: '/einvoice/portal/mapping-builder', permKey: 'mapping_builder' },
  { label: 'INTEGRATION DOCS', icon: Plug, path: '/einvoice/portal/integration-docs', permKey: 'integration_docs' },
  { label: 'SETTINGS', icon: Settings, path: '/einvoice/portal/settings', permKey: 'settings' },
];

export default function PortalLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [propertySearch, setPropertySearch] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('einvoice_theme');
    return saved ? saved === 'dark' : true;
  });
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('einvoice_token') || localStorage.getItem('unified_token') || localStorage.getItem('token');

  useEffect(() => { if (!token) navigate('/auth/login'); }, [token]);
  useEffect(() => { if (token) { loadProperties(); loadProfile(); } }, [token]);
  // After returning from Stripe checkout, confirm the session so the subscription activates immediately
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('session_id');
    if (token && sid) {
      axios.get(`${API_URL}/api/einvoice/payments/status/${sid}`, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {})
        .finally(() => { loadProfile(); });
    }
  }, [token]);
  useEffect(() => { localStorage.setItem('einvoice_theme', isDark ? 'dark' : 'light'); }, [isDark]);

  // Listen for page-level property changes to keep header in sync
  useEffect(() => {
    const onPropertyChanged = (e) => {
      const prop = e.detail;
      setSelectedProperty(prop || null);
    };
    window.addEventListener('property-changed', onPropertyChanged);
    return () => window.removeEventListener('property-changed', onPropertyChanged);
  }, []);

  const loadProfile = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/einvoice/portal/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.avatar_url) setAvatarUrl(`${API_URL}${data.avatar_url}?t=${Date.now()}`);
      // Access / trial gate — authoritative source
      try {
        const acc = await axios.get(`${API_URL}/api/einvoice/payments/access`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const a = acc.data || {};
        setTrialStatus({
          trial_active: !!a.in_trial,
          days_remaining: a.trial_days_left || 0,
          trial_end: a.trial_ends_at,
          show_payment_gate: a.access === false,
        });
      } catch (e) { /* ignore */ }
    } catch (err) { /* ignore */ }
  };

  const loadProperties = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/einvoice/portal/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const props = data.properties || [];
      setProperties(props);
      const saved = localStorage.getItem('einvoice_selected_property');
      if (saved) setSelectedProperty(props.find(p => p.id === saved) || null);
    } catch (err) { console.error('Failed to load properties:', err); }
  };

  const selectProperty = (prop) => {
    setSelectedProperty(prop);
    if (prop) localStorage.setItem('einvoice_selected_property', prop.id);
    else localStorage.removeItem('einvoice_selected_property');
    setPropertyDropdownOpen(false);
    window.dispatchEvent(new CustomEvent('property-changed', { detail: prop }));
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      e.target.value = '';
      return;
    }
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, or WebP images are allowed');
      e.target.value = '';
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/einvoice/portal/auth/avatar`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.avatar_url) {
        setAvatarUrl(`${API_URL}${data.avatar_url}?t=${Date.now()}`);
        toast.success('Profile photo updated');
      }
    } catch (err) {
      console.error('Avatar upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    }
    e.target.value = '';
    setUserDropdownOpen(false);
  };

  const handleRemoveAvatar = async () => {
    try {
      await axios.delete(`${API_URL}/api/einvoice/portal/auth/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvatarUrl(null);
      toast.success('Profile photo removed');
    } catch (err) { 
      console.error('Remove avatar failed:', err);
      toast.error('Failed to remove photo');
    }
    setUserDropdownOpen(false);
  };

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('einvoice_user') || localStorage.getItem('unified_user') || localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();

  // Trial status tracking (sourced from authenticated /auth/me profile)
  const [trialStatus, setTrialStatus] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null); // null = loading, {} = loaded

  // Load user role permissions
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/einvoice/portal/auth/permissions`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => setUserPermissions(r.data?.permissions || {}))
      .catch(() => setUserPermissions({})); // No permissions = full access (backward compat)
  }, [token]);

  // Filter nav items based on permissions
  const HIDDEN_NAV_KEYS = ['mapping_builder', 'integration_docs'];
  const filteredNavItems = NAV_ITEMS.filter(item => {
    if (HIDDEN_NAV_KEYS.includes(item.permKey)) return false; // Hidden from e-Invoice sidebar
    if (!userPermissions || Object.keys(userPermissions).length === 0) return true; // No RBAC = show all
    if (userPermissions._full_access) return true; // Super admin
    const einvoicePerms = userPermissions.staymii_einvoice || {};
    const pagePerms = einvoicePerms[item.permKey];
    if (!pagePerms) return false; // No permission entry = hidden
    return pagePerms.view === true; // Must have view permission
  });

  const handleLogout = () => {
    localStorage.removeItem('einvoice_token');
    localStorage.removeItem('einvoice_user');
    localStorage.removeItem('unified_token');
    localStorage.removeItem('unified_user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('einvoice_selected_property');
    navigate('/auth/login');
  };

  const isActive = (path) => location.pathname === path;
  const initials = (user.full_name || 'U')[0].toUpperCase();

  const Avatar = ({ size = 'md' }) => {
    const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-9 h-9 text-sm', lg: 'w-16 h-16 text-2xl' };
    return avatarUrl ? (
      <img src={avatarUrl} alt="Avatar" className={`${sizes[size].split(' ').slice(0, 2).join(' ')} rounded-full object-cover`} data-testid="user-avatar-img" />
    ) : (
      <div className={`${sizes[size]} rounded-full bg-blue-100 flex items-center justify-center`} data-testid="user-avatar-initial">
        <span className="font-bold text-[#4169E1]">{initials}</span>
      </div>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-[#181B30]">
      <div className="flex flex-col px-4 pt-3 pb-2 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#4169E1] flex items-center justify-center shrink-0" data-testid="sidebar-logo">
            <Building2 className="w-[18px] h-[18px] text-white" />
          </div>
          <span className="font-bold text-white text-base tracking-wide">StayMii <span className="whitespace-nowrap">e-Invoice</span></span>
        </div>
        <p className="text-[9px] text-gray-400 mt-1.5 leading-tight">e-Invoice &amp; LHDN Compliance Module</p>
      </div>
      <nav className="flex-1 py-3 px-3 space-y-1.5 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            className={`flex items-center gap-3 px-3 py-3 rounded-lg text-[11px] font-semibold tracking-wider transition-colors group ${
              isActive(item.path)
                ? 'bg-[#4169E1]/20 text-white'
                : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
            }`}
          >
            <item.icon className={`w-[18px] h-[18px] shrink-0 ${isActive(item.path) ? 'text-[#4169E1]' : 'text-gray-500 group-hover:text-gray-300'}`} />
            <span className="flex-1 leading-tight">{item.label}</span>
            <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${isActive(item.path) ? 'text-[#4169E1]' : 'text-gray-600 group-hover:text-gray-400'}`} />
          </Link>
        ))}
      </nav>
      <div className="px-3 pb-4 shrink-0 space-y-1">
        <button
          onClick={() => setIsDark(!isDark)}
          className="flex items-center gap-3 px-3 py-3 rounded-lg text-[11px] font-semibold tracking-wider transition-colors w-full text-gray-400 hover:bg-white/5 hover:text-gray-200 group"
          data-testid="theme-toggle-sidebar"
        >
          {isDark ? <Sun className="w-[18px] h-[18px] shrink-0 text-amber-400" /> : <Moon className="w-[18px] h-[18px] shrink-0 text-gray-500 group-hover:text-gray-300" />}
          <span className="flex-1 text-left leading-tight">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
        <button
          onClick={() => { window.location.href = '/module-selector'; }}
          className="flex items-center gap-3 px-3 py-3 rounded-lg text-[11px] font-semibold tracking-wider transition-colors w-full text-gray-400 hover:bg-white/5 hover:text-gray-200 group"
          data-testid="einvoice-switch-module"
        >
          <LayoutGrid className="w-[18px] h-[18px] shrink-0 text-gray-500 group-hover:text-gray-300" />
          <span className="flex-1 text-left leading-tight">Switch Module</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className={`${isDark ? 'dark' : ''} flex overflow-hidden`} style={{ height: '100dvh' }} data-testid="portal-layout">
      <input type="file" ref={fileInputRef} className="hidden" accept="image/png,image/jpeg,image/webp" onChange={handleAvatarUpload} data-testid="avatar-file-input" />

      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-[200px] shrink-0 h-full" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
          <div className="lg:hidden fixed top-0 left-0 bottom-0 w-[200px] z-50 shadow-2xl">
            <SidebarContent />
          </div>
        </>
      )}

      {/* Right column */}
      <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
        <header className="bg-[#181B30] h-[60px] flex items-center justify-between px-6 shrink-0 border-b border-white/5" data-testid="portal-topbar">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-gray-400 hover:text-white" data-testid="mobile-sidebar-btn">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="lg:hidden w-8 h-8 rounded-lg bg-[#4169E1] flex items-center justify-center shrink-0">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-bold text-lg">StayMii <span className="text-[#4169E1]">e-Invoice</span></span>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative hidden lg:block mr-2">
              <button onClick={() => setPropertyDropdownOpen(!propertyDropdownOpen)} className="flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-lg hover:bg-white/5 transition-colors text-sm" data-testid="property-switcher">
                <Building2 className="w-4 h-4 text-gray-400" />
                <span className="text-gray-200 font-medium max-w-[160px] truncate">{selectedProperty ? selectedProperty.property_name : 'All Properties'}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${propertyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {propertyDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => { setPropertyDropdownOpen(false); setPropertySearch(''); }} />
                  <div className="absolute top-full right-0 mt-1 w-72 bg-[#1f2937] border border-white/10 rounded-xl shadow-2xl z-20 py-1" data-testid="property-dropdown">
                    {properties.length > 3 && (
                      <div className="px-3 pt-2 pb-1">
                        <input
                          type="text"
                          value={propertySearch}
                          onChange={e => setPropertySearch(e.target.value)}
                          placeholder="Search properties..."
                          className="w-full h-8 rounded-lg bg-white/5 border border-white/10 px-3 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-[#4169E1]/50"
                          data-testid="property-search-input"
                          autoFocus
                        />
                      </div>
                    )}
                    <button onClick={() => { selectProperty(null); setPropertySearch(''); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/5 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center shrink-0"><Building2 className="w-4 h-4 text-gray-300" /></div>
                      <span className="text-sm text-gray-200 font-medium flex-1">All Properties</span>
                      {!selectedProperty && <Check className="w-4 h-4 text-[#4169E1]" />}
                    </button>
                    {properties.length > 0 && <div className="border-t border-white/5 my-1" />}
                    {properties
                      .filter(p => !propertySearch || (p.property_name || '').toLowerCase().includes(propertySearch.toLowerCase()) || (p.property_code || '').toLowerCase().includes(propertySearch.toLowerCase()))
                      .map(prop => (
                      <button key={prop.id} onClick={() => { selectProperty(prop); setPropertySearch(''); }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/5 transition-colors ${selectedProperty?.id === prop.id ? 'bg-[#4169E1]/10' : ''}`}>
                        <div className={`w-8 h-8 rounded-lg ${selectedProperty?.id === prop.id ? 'bg-[#4169E1]/30' : 'bg-[#4169E1]/20'} flex items-center justify-center shrink-0`}><Building2 className="w-4 h-4 text-[#4169E1]" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-200 font-medium truncate">{prop.property_name}</p>
                          <p className="text-xs text-gray-500 font-mono truncate">{prop.property_code}</p>
                        </div>
                        {selectedProperty?.id === prop.id && <Check className="w-4 h-4 text-[#4169E1] shrink-0" />}
                      </button>
                    ))}
                    {properties.length > 3 && propertySearch && properties.filter(p => (p.property_name || '').toLowerCase().includes(propertySearch.toLowerCase()) || (p.property_code || '').toLowerCase().includes(propertySearch.toLowerCase())).length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-3">No properties match "{propertySearch}"</p>
                    )}
                  </div>
                </>
              )}
            </div>

            <button className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors hidden lg:flex" data-testid="header-grid-btn">
              <LayoutGrid className="w-[18px] h-[18px]" />
            </button>

            <NotificationBell />

            <div className="h-8 w-px bg-white/10 mx-1 hidden lg:block" />

            {/* User with Avatar */}
            <div className="relative">
              <button onClick={() => setUserDropdownOpen(!userDropdownOpen)} className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-lg hover:bg-white/5 transition-colors" data-testid="user-menu-btn">
                <Avatar size="md" />
                <div className="hidden lg:block text-left">
                  <p className="text-sm font-medium text-white leading-tight">{user.full_name || 'User'}</p>
                  <p className="text-[11px] text-gray-400 leading-tight capitalize">{user.role || 'Admin'}</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 hidden lg:block" />
              </button>
              {userDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setUserDropdownOpen(false)} />
                  <div className="absolute top-full right-0 mt-1 w-56 bg-[#1f2937] border border-white/10 rounded-xl shadow-2xl z-20 py-1" data-testid="user-dropdown">
                    {/* Avatar section */}
                    <div className="px-4 py-3 border-b border-white/5 flex flex-col items-center">
                      <div className="relative group cursor-pointer" onClick={() => { fileInputRef.current?.click(); setUserDropdownOpen(false); }}>
                        <Avatar size="lg" />
                        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <p className="text-sm text-white font-medium mt-2 truncate max-w-full">{user.full_name}</p>
                      <p className="text-xs text-gray-400 truncate max-w-full">{user.email}</p>
                    </div>
                    <button onClick={() => { setUserDropdownOpen(false); setProfileModalOpen(true); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 transition-colors" data-testid="portal-menu-profile">
                      <User className="w-4 h-4" /> My Profile
                    </button>
                    <button
                      onClick={() => { fileInputRef.current?.click(); setUserDropdownOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                      data-testid="change-avatar-btn"
                    >
                      <Camera className="w-4 h-4" /> Change Avatar
                    </button>
                    {avatarUrl && (
                      <button
                        onClick={handleRemoveAvatar}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                        data-testid="remove-avatar-btn"
                      >
                        <X className="w-4 h-4" /> Remove Profile Photo
                      </button>
                    )}
                    <button onClick={() => { setUserDropdownOpen(false); navigate('/einvoice/portal/settings'); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 transition-colors">
                      <Settings className="w-4 h-4" /> Settings
                    </button>
                    <div className="border-t border-white/5 my-1" />
                    <button onClick={() => { setUserDropdownOpen(false); handleLogout(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-900/20 transition-colors" data-testid="dropdown-logout-btn">
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 min-w-0 overflow-y-auto bg-slate-50 dark:bg-black">
          {/* Trial expiry payment gate — blocks the whole portal (except billing pages) */}
          {trialStatus?.show_payment_gate && !location.pathname.includes('/subscription') && !location.pathname.includes('/billing') && (
            <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="trial-payment-gate">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Free Trial Expired</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  Your 14-day free trial has ended. Please subscribe to continue using StayMii e-Invoice.
                </p>
                <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3 mb-6 text-sm">
                  <p className="text-slate-500 dark:text-slate-400 text-xs">
                    Trial ended: {trialStatus?.trial_end ? new Date(trialStatus.trial_end).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A'}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/einvoice/portal/subscription')}
                  className="inline-block w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
                  data-testid="subscribe-now-btn"
                >
                  Subscribe Now
                </button>
                <button
                  onClick={handleLogout}
                  className="mt-2 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  data-testid="gate-logout-btn"
                >
                  Log out
                </button>
                <p className="text-xs text-slate-400 mt-3">
                  Contact <a href="mailto:support@staymii.com" className="text-blue-500 hover:underline">support@staymii.com</a> for help
                </p>
              </div>
            </div>
          )}

          {/* Persistent trial banner — shown throughout an active trial */}
          {trialStatus?.trial_active && (
            <div
              className={`border-b px-4 py-2.5 flex items-center justify-between ${
                trialStatus.days_remaining <= 3
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                  : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
              }`}
              data-testid="trial-banner"
            >
              <div className="flex items-center gap-2 text-sm">
                <Clock className={`w-4 h-4 ${trialStatus.days_remaining <= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} />
                <span className={`font-medium ${trialStatus.days_remaining <= 3 ? 'text-amber-800 dark:text-amber-300' : 'text-blue-800 dark:text-blue-300'}`} data-testid="trial-days-remaining">
                  {trialStatus.days_remaining <= 0
                    ? 'Your free trial expires today!'
                    : `${trialStatus.days_remaining} day${trialStatus.days_remaining !== 1 ? 's' : ''} left in your free trial`}
                </span>
              </div>
              <button
                onClick={() => navigate('/einvoice/portal/subscription')}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                data-testid="upgrade-now-btn"
              >
                Upgrade Now
              </button>
            </div>
          )}

          {children}
        </main>

        <PlatformFooter />
      </div>

      {profileModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={() => setProfileModalOpen(false)} data-testid="portal-profile-modal">
          <div className="bg-[#181B30] border border-white/10 rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg">My Profile</h3>
              <button onClick={() => setProfileModalOpen(false)} className="text-gray-400 hover:text-white" data-testid="portal-profile-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                {avatarUrl
                  ? <img src={avatarUrl} alt="Avatar" className="w-20 h-20 rounded-full object-cover" />
                  : <span className="font-bold text-white text-2xl">{initials}</span>}
              </div>
              <div className="text-center">
                <p className="font-semibold text-white" data-testid="portal-profile-name">{user.full_name || 'User'}</p>
                <p className="text-sm text-gray-400" data-testid="portal-profile-email">{user.email || ''}</p>
                <p className="text-xs text-gray-500 mt-1 capitalize">{user.role || 'Admin'}</p>
              </div>
              <button onClick={() => { setProfileModalOpen(false); fileInputRef.current?.click(); }} className="mt-1 text-sm text-blue-400 hover:underline" data-testid="portal-profile-change-avatar">Change avatar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
